import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import styled, { css } from 'styled-components';
import { ObjectFit, ObjectPosition } from '@westwing/ui-kit/Picture/Image';
import UIkitPicture from '@westwing/ui-kit/Picture';
import AspectRatio from '../Layout/AspectRatio';
import { useAppShell } from '@ids/appshell';
import { IDSContentfulImageFile } from '@ids/types/contentful';

const screenSizes = [768, 1024, 1200, 1440, 1920];

export const makeSourcesSet = (quality: number, src: string): { webpSrcSet: string; jpegSrcSet: string } => {
    return screenSizes.reduce(
        (acc, screenSize) => {
            const webpSrc = `${src}?w=${screenSize}&q=${quality}&fm=webp`;
            acc.webpSrcSet = `${acc.webpSrcSet}${acc.webpSrcSet ? ', ' : ''}${webpSrc} ${screenSize}w`;

            const jpegSrc = `${src}?w=${screenSize}&q=${quality}&fm=jpg`;
            acc.jpegSrcSet = `${acc.jpegSrcSet}${acc.jpegSrcSet ? ', ' : ''}${jpegSrc} ${screenSize}w`;

            return acc;
        },
        {
            webpSrcSet: '',
            jpegSrcSet: '',
        }
    );
};

const imageStyles = css`
    img {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        box-sizing: border-box;
        padding: 0;
        border: none;
        margin: auto;
        display: block;
        width: 0;
        height: 0;
        min-width: 100%;
        max-width: 100%;
        min-height: 100%;
        max-height: 100%;
    }
`;

export type StyledImageWrapperProps =
    | {
          width?: never;
          height?: never;
          layout?: 'fill';
      }
    | {
          width: number | string;
          height: number | string;
          layout?: 'responsive' | 'intrinsic';
      };

const StyledImageWrapper = styled.div<StyledImageWrapperProps>`
    --width: ${({ width }) => width};
    --height: ${({ height }) => height};

    ${({ layout }) => {
        if (layout === 'fill') {
            return css`
                ${imageStyles}

                display: block;
                overflow: hidden;
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                right: 0;
                box-sizing: border-box;
                margin: 0;

                img {
                    object-fit: cover;
                }
            `;
        }
        if (layout === 'responsive') {
            return css`
                ${imageStyles}

                display: block;
                overflow: hidden;
                position: relative;
                box-sizing: border-box;
                margin: 0;
                padding-top: calc(100% / (var(--width) / var(--height)));
            `;
        }

        return null;
    }}
`;

type MediaImageLayoutWrapperProps = StyledImageWrapperProps & {
    ratio?: number | string;
    maxWidth?: number | string;
};

const MediaImageLayoutWrapper = ({
    children,
    maxWidth,
    ...restProps
}: React.PropsWithChildren<MediaImageLayoutWrapperProps>) => {
    const wrapperProps = { layout: restProps.layout } as StyledImageWrapperProps;

    if (restProps.layout === 'responsive') {
        wrapperProps.width = restProps.width;
        wrapperProps.height = restProps.height;
    }

    const ratio =
        typeof restProps.width === 'number' && typeof restProps.height === 'number'
            ? restProps.width / restProps.height
            : 1;

    if (wrapperProps.layout === 'intrinsic') {
        return (
            <AspectRatio ratio={ratio} maxWidth={maxWidth}>
                <StyledImageWrapper {...wrapperProps}>{children}</StyledImageWrapper>
            </AspectRatio>
        );
    }
    return <StyledImageWrapper {...wrapperProps}>{children}</StyledImageWrapper>;
};

export type ImageProps = React.ImgHTMLAttributes<HTMLImageElement> &
    StyledImageWrapperProps & {
        /**
         * Accepts a boolean value.
         * If true it will preload the picture component.
         *
         * @default false
         */
        priority?: boolean;
        /**
         * Accepts a number value which alters the quality of the image
         *
         * @default 70
         */
        quality?: number;
        maxWidth?: number | string;
        objectFit?: ObjectFit;
        objectPosition?: ObjectPosition;
    };

export type MediaImageProps = Omit<ImageProps, keyof MediaImageLayoutWrapperProps> & {
    image?: IDSContentfulImageFile;
    'data-testid'?: string;
};

const MediaImage = ({
    priority,
    quality = 70,
    sizes = '100vw',
    image,
    objectFit,
    objectPosition,
    'data-testid': testId,
    ...imageProps
}: MediaImageProps) => {
    const { logger } = useAppShell();

    const src = image?.file?.url || imageProps.src;

    if (!src) {
        return null;
    }

    const logImageErrorWarning = () => {
        logger.warn(`[Contentful] Interior Design Service Image with src - "${src}" failed to load`);
    };

    const { webpSrcSet, jpegSrcSet } = makeSourcesSet(quality, src);

    return (
        <React.Fragment>
            {priority && (
                <Helmet>
                    <link rel="preload" as="image" imageSrcSet={webpSrcSet} sizes={sizes} />
                </Helmet>
            )}
            <UIkitPicture
                testId={testId}
                objectFit={objectFit}
                objectPosition={objectPosition}
                sources={
                    <React.Fragment>
                        <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
                        <source type="image/jpeg" srcSet={jpegSrcSet} sizes={sizes} />
                    </React.Fragment>
                }
                loading={priority ? 'eager' : 'lazy'}
                fallback={{ url: src, className: imageProps.className, alt: imageProps?.alt || image?.title }}
                errorFunction={logImageErrorWarning}
            />
        </React.Fragment>
    );
};

type MediaImageWithLayoutProps = MediaImageProps & MediaImageLayoutWrapperProps;

const MediaImageWithLayout = ({
    layout = 'fill',
    width,
    height,
    maxWidth,
    children = null,
    ...mediaImageProps
}: MediaImageWithLayoutProps) => (
    <MediaImageLayoutWrapper maxWidth={maxWidth} {...({ layout, width, height } as StyledImageWrapperProps)}>
        {children}
        <MediaImage {...mediaImageProps} />
    </MediaImageLayoutWrapper>
);

MediaImage.Full = MediaImageWithLayout;

export { MediaImageWithLayout as default, MediaImage };
